import { useEffect } from 'react'

/**
 * Creates a linear arrow-key handler for a focusable list.
 *
 * @param {{
 *  getFocusables: () => HTMLElement[],
 *  nextKey: string,
 *  prevKey: string,
 *  wrap?: boolean,
 *  onNextBoundary?: () => boolean,
 *  onPrevBoundary?: () => boolean,
 * }} config
 * @returns {(e: React.KeyboardEvent) => boolean}
 */
export const createLinearArrowKeyHandler = ({
  getFocusables,
  nextKey,
  prevKey,
  wrap = true,
  onNextBoundary,
  onPrevBoundary,
}) => {
  return (e) => {
    if (e.key !== nextKey && e.key !== prevKey) return false

    e.preventDefault()
    const focusables = getFocusables()
    if (!focusables.length) return true

    const idx = focusables.indexOf(document.activeElement)

    if (e.key === nextKey) {
      if (idx >= focusables.length - 1) {
        if (onNextBoundary?.()) return true
        if (wrap) {
          focusables[0]?.focus()
        } else {
          focusables[focusables.length - 1]?.focus()
        }
        return true
      }

      const next = idx === -1 ? focusables[0] : focusables[idx + 1]
      next?.focus()
      return true
    }

    if (idx <= 0) {
      if (onPrevBoundary?.()) return true
      if (wrap) {
        focusables[focusables.length - 1]?.focus()
      } else {
        focusables[0]?.focus()
      }
      return true
    }

    focusables[idx - 1]?.focus()
    return true
  }
}

/**
 * Generic directional region navigation engine.
 * Regions provide focusable resolvers while boundary routing defines cross-region jumps.
 *
 * @param {{
 *  isEnabled?: boolean,
 *  keys?: string[],
 *  getRegionForElement: (activeElement: HTMLElement) => string|null,
 *  getFocusablesForRegion: (region: string) => HTMLElement[],
 *  handleKeyForRegion?: (context: {
 *    region: string,
 *    key: string,
 *    activeElement: HTMLElement,
 *    focusables: HTMLElement[],
 *    index: number,
 *  }) => boolean,
 *  onBoundary?: (context: {
 *    region: string,
 *    key: string,
 *    activeElement: HTMLElement,
 *    focusables: HTMLElement[],
 *    index: number,
 *  }) => boolean,
 *  shouldWrapOnBoundary?: (context: { region: string, key: string }) => boolean,
 *  isEditableElement?: (element: HTMLElement) => boolean,
 * }} config
 */
const useDirectionalRegionNavigation = ({
  isEnabled = true,
  keys = ['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'],
  getRegionForElement,
  getFocusablesForRegion,
  handleKeyForRegion,
  onBoundary,
  shouldWrapOnBoundary,
  isEditableElement,
}) => {
  useEffect(() => {
    if (!isEnabled || typeof window === 'undefined') return

    const handleKeyDown = (e) => {
      if (!keys.includes(e.key)) return

      const activeElement = document.activeElement
      if (!(activeElement instanceof HTMLElement)) return
      if (isEditableElement?.(activeElement)) return

      const region = getRegionForElement(activeElement)
      if (!region) return

      const focusables = getFocusablesForRegion(region)
      if (!focusables.length) return

      const index = focusables.indexOf(activeElement)
      const context = {
        region,
        key: e.key,
        activeElement,
        focusables,
        index,
      }

      if (handleKeyForRegion?.(context)) {
        e.preventDefault()
        return
      }

      const isForward = e.key === 'ArrowDown' || e.key === 'ArrowRight'
      e.preventDefault()

      if (isForward) {
        if (index >= focusables.length - 1) {
          if (onBoundary?.(context)) return

          const wrap = shouldWrapOnBoundary?.({ region, key: e.key }) ?? false
          if (wrap) {
            focusables[0]?.focus()
          } else {
            focusables[focusables.length - 1]?.focus()
          }
          return
        }

        const next = index === -1 ? focusables[0] : focusables[index + 1]
        next?.focus()
        return
      }

      if (index <= 0) {
        if (onBoundary?.(context)) return

        const wrap = shouldWrapOnBoundary?.({ region, key: e.key }) ?? false
        if (wrap) {
          focusables[focusables.length - 1]?.focus()
        } else {
          focusables[0]?.focus()
        }
        return
      }

      focusables[index - 1]?.focus()
    }

    window.addEventListener('keydown', handleKeyDown, true)
    return () => window.removeEventListener('keydown', handleKeyDown, true)
  }, [
    getFocusablesForRegion,
    getRegionForElement,
    handleKeyForRegion,
    isEditableElement,
    isEnabled,
    keys,
    onBoundary,
    shouldWrapOnBoundary,
  ])
}

export default useDirectionalRegionNavigation
